/**
 * API service for communicating with the backend.
 */
import axios from 'axios';
import type {
  PDFUploadResponse,
  FeedbackRequest,
  FeedbackResponse,
  DocumentFeedbackRequest,
  DocumentFeedbackResponse,
  PageTextResponse,
  AcceptResponse,
  EditResponse,
  RejectResponse,
  CategoryDismissResponse,
  CommentsListResponse,
  ManualCommentRequest,
  Comment,
} from '../types';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000',
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Upload a PDF file for processing.
 */
export async function uploadPDF(file: File): Promise<PDFUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post<PDFUploadResponse>('/api/pdf/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
}

/**
 * Generate AI feedback for a page of student work.
 */
export async function generateFeedback(
  request: FeedbackRequest
): Promise<FeedbackResponse> {
  const response = await api.post<FeedbackResponse>('/api/feedback/generate', request);
  return response.data;
}

/**
 * Generate AI feedback for an entire document.
 */
export async function generateDocumentFeedback(
  request: DocumentFeedbackRequest
): Promise<DocumentFeedbackResponse> {
  const response = await api.post<DocumentFeedbackResponse>(
    '/api/feedback/generate-document',
    request
  );
  return response.data;
}

/**
 * Get all comments for a submission.
 */
export async function getComments(
  submissionId: string
): Promise<{ submission_id: string; comments: unknown[] }> {
  const response = await api.get(`/api/feedback/${submissionId}`);
  return response.data;
}

/**
 * Get extracted text for a specific page.
 */
export async function getPageText(
  submissionId: string,
  pageNumber: number
): Promise<PageTextResponse> {
  const response = await api.get<PageTextResponse>(
    `/api/pdf/${submissionId}/page/${pageNumber}`
  );
  return response.data;
}

/**
 * Get all comments for a submission from the comments API.
 */
export async function getCommentsFromApi(
  submissionId: string
): Promise<CommentsListResponse> {
  const response = await api.get<CommentsListResponse>(`/api/comments/${submissionId}`);
  return response.data;
}

/**
 * Accept a comment.
 */
export async function acceptComment(commentId: string): Promise<AcceptResponse> {
  const response = await api.post<AcceptResponse>(`/api/comments/${commentId}/accept`);
  return response.data;
}

/**
 * Edit a comment's text.
 */
export async function editComment(
  commentId: string,
  newText: string
): Promise<EditResponse> {
  const response = await api.post<EditResponse>(`/api/comments/${commentId}/edit`, {
    new_text: newText,
  });
  return response.data;
}

/**
 * Reject a comment with a reason.
 */
export async function rejectComment(
  commentId: string,
  reason: string
): Promise<RejectResponse> {
  const response = await api.post<RejectResponse>(`/api/comments/${commentId}/reject`, {
    reason,
  });
  return response.data;
}

/**
 * Dismiss all comments of a category for a submission.
 */
export async function dismissCategory(
  submissionId: string,
  category: string
): Promise<CategoryDismissResponse> {
  const response = await api.post<CategoryDismissResponse>('/api/comments/dismiss-category', {
    submission_id: submissionId,
    category,
  });
  return response.data;
}

/**
 * Create a manual comment.
 */
export async function createManualComment(
  request: ManualCommentRequest
): Promise<Comment> {
  const response = await api.post<Comment>('/api/comments/manual', request);
  return response.data;
}

export default api;
